
import React from "react";
import { Classname } from "@rivtn/noseur";
import Btn from "../common/Btn";
import { i18nManager } from "../../i18n";
import styles from "../../assets/style.module.css";
import { ITeamNews, ITeamWebsite } from "../../../../../services";
import { isPageVisible, pagePath, stripMarkdown, teamBanner, teamLogo } from "../../helpers";

interface ISlide {
    key: string;
    title: string;
    text?: string;
    image?: string;
    cta?: { label: string; to: string; search?: string; };
}

export interface HeroSliderProps {
    basePath: string;
    news?: ITeamNews[];
    website: ITeamWebsite;
}

const AUTOPLAY_MS = 6500;

function HeroSlider({ website, basePath, news }: HeroSliderProps) {

    const labels = i18nManager.Labels;
    const [active, setActive] = React.useState(0);
    const banner = teamBanner(website) ?? teamLogo(website);
    const slides: ISlide[] = [{
        key: "team",
        image: banner,
        title: website.team.name,
        text: website.team.description,
        cta: isPageVisible(website, "MEMBERS") ? { label: labels.home.meet_squad, to: pagePath(basePath, "MEMBERS") } : undefined,
    }, ...(news ?? []).filter((n) => !!n.extra_data?.featured_image).slice(0, 4).map((n) => ({
        key: n.external_id,
        title: n.title,
        image: n.extra_data.featured_image,
        text: stripMarkdown(n.content, 110),
        cta: { label: labels.read_more, to: pagePath(basePath, "NEWS"), search: `article=${n.external_id}` },
    }))];

    React.useEffect(() => {
        if (slides.length < 2) return;
        const timer = window.setTimeout(() => setActive((a) => (a + 1) % slides.length), AUTOPLAY_MS);
        return () => window.clearTimeout(timer);
    }, [active, slides.length]);

    return (<section className={styles["hero-section"]}>
        <div className={styles["bg-overlay"]} />
        <div className={styles["container"]}>
            <div className={styles["hero"]}>
                {slides.map((s, i) => (<div key={s.key} className={Classname.build(styles["hero-slide"], { [styles["active"]]: i === active })} aria-hidden={i !== active}>
                    {s.image ? (<img className={Classname.build(styles["hero-img"], { [styles["hero-img-contain"]]: s.key === "team" && !teamBanner(website) })} src={s.image} alt="" />) : (<div className={styles["hero-ph"]} />)}
                    <div className={styles["hero-dim"]} />
                    <div className={styles["hero-content"]}>
                        {s.key === "team" && (<span className={styles["hero-kicker"]}>{labels.home.official}</span>)}
                        <h2 className={Classname.build(styles["d2"], styles["clamp-2"])}>{s.title}</h2>
                        {s.text && (<p className={Classname.build(styles["hero-text"], styles["clamp-2"])}>{s.text}</p>)}
                        {s.cta && (<Btn to={{ pathname: s.cta.to, search: s.cta.search }}>{s.cta.label}</Btn>)}
                    </div>
                </div>))}
                {slides.length > 1 && (<div className={styles["hero-dots"]}>
                    {slides.map((s, i) => (<button key={s.key} aria-label={`${i + 1}`} className={Classname.build(styles["hero-dot"], { [styles["active"]]: i === active })} onClick={() => setActive(i)} />))}
                </div>)}
            </div>
        </div>
    </section>);
}

export default HeroSlider;
